import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useState, useEffect, useRef, useCallback } from 'react'
import { FiSearch, FiChevronUp, FiExternalLink, FiMenu, FiRefreshCw, FiLogOut, FiZap } from 'react-icons/fi'
import { ROUTES } from '@/constants/routes'
import Logo from '@/components/Logo'
import NotificationModal from '@/components/ui/NotificationModal'
import CurrencyDropdown from '@/components/CurrencyDropdown'
import { useAuthOptional } from '@/contexts/useAuth'
import { getAuthToken } from '@/utils/auth'
import { getExchangeRate } from '@/services/api/exchangeRates'
import { formatCurrency } from '@/utils/currencyFormat'
import { getUnreadCount } from '@/services/api/notifications'

interface HeaderProps {
  onMenuClick?: () => void
}

/** Returns the module base path for header search: search applies to current module only. */
function getSearchTargetPath(pathname: string): string {
  const path = pathname.split('?')[0]
  if (path.startsWith('/shipments')) return ROUTES.SHIPMENTS
  if (path.startsWith('/clearing')) return ROUTES.CLEARING
  if (path.startsWith('/contracts')) {
    const parts = path.split('/').filter(Boolean)
    if (parts.length >= 2 && ['sale', 'buy', 'performance', 'new'].includes(parts[1])) return `/${parts[0]}/${parts[1]}`
    return ROUTES.CONTRACTS
  }
  if (path.startsWith('/inventory')) return ROUTES.INVENTORY
  if (path.startsWith('/load-out')) return ROUTES.LOAD_OUT
  if (path.startsWith('/order-fulfillment')) return ROUTES.ORDER_FULFILLMENT
  if (path.startsWith('/finance/invoices')) return ROUTES.FINANCE_INVOICES
  if (path.startsWith('/finance/products-services')) return ROUTES.FINANCE_PRODUCTS_SERVICES
  if (path.startsWith('/finance/')) return ROUTES.FINANCE_OVERVIEW
  return ROUTES.SHIPMENTS
}

const Header = ({ onMenuClick }: HeaderProps = {}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const auth = useAuthOptional()
  const [searchQuery, setSearchQuery] = useState('')
  const [isCurrencyOpen, setIsCurrencyOpen] = useState(false)
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false)
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0)
  const [fromCurrency, setFromCurrency] = useState('USD')
  const [toCurrency, setToCurrency] = useState('ZAR')
  const [exchangeRate, setExchangeRate] = useState<number | null>(null)
  const [fromAmount, setFromAmount] = useState(1)
  const [toAmount, setToAmount] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const currencyRef = useRef<HTMLDivElement>(null)

  const runSearch = useCallback(() => {
    const q = searchQuery.trim()
    const targetPath = getSearchTargetPath(location.pathname)
    if (q) navigate(`${targetPath}?search=${encodeURIComponent(q)}`)
    else navigate(targetPath)
  }, [location.pathname, navigate, searchQuery])

  // Keep header search in sync with current URL query.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    setSearchQuery(params.get('search') ?? '')
  }, [location.pathname, location.search])

  // Swap currencies function
  const swapCurrencies = useCallback(() => {
    setFromCurrency(toCurrency)
    setToCurrency(fromCurrency)
    // Reset amount to 1 when swapping
    setFromAmount(1)
  }, [fromCurrency, toCurrency])

  // Fetch exchange rate
  const fetchExchangeRate = useCallback(async () => {
    // Don't fetch if currencies are the same
    if (fromCurrency === toCurrency) {
      setExchangeRate(1)
      setToAmount(fromAmount)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)
    try {
      console.log(`[Exchange Rate] Fetching: ${fromCurrency} → ${toCurrency}`)
      const rateData = await getExchangeRate(fromCurrency, toCurrency)
      console.log(`[Exchange Rate] Success: ${fromCurrency}/${toCurrency} = ${rateData.rate}`)
      setExchangeRate(rateData.rate)
      
      // Calculate converted amount
      const converted = fromAmount * rateData.rate
      setToAmount(converted)
    } catch (err) {
      // Check if it's a connection error (backend not running)
      const isConnectionError = err instanceof TypeError && (err as Error).message.includes('Failed to fetch')
      // Check if it's an authentication error (401)
      const errorMessage = err instanceof Error ? err.message : String(err)
      const isAuthError = errorMessage.includes('Unauthorized') || errorMessage.includes('Invalid token')
      
      if (isConnectionError) {
        // Backend not available - show error but don't use fallback
        setError('Connection error')
        console.log('Exchange rate: Backend not available')
        // Don't set a rate - let user see the error
        setExchangeRate(null)
        setToAmount(null)
      } else if (isAuthError) {
        // User not logged in - try test endpoint
        console.log('Exchange rate: User not authenticated, trying test endpoint')
        // The API service already handles this by using test endpoints
        // If it still fails, show error
        setError('Authentication required')
        setExchangeRate(null)
        setToAmount(null)
      } else {
        setError('Failed to fetch exchange rate')
        console.error('Exchange rate error:', err)
        setExchangeRate(null)
        setToAmount(null)
      }
    } finally {
      setLoading(false)
    }
  }, [fromCurrency, toCurrency, fromAmount])

  // Fetch rate on mount and when currencies change (works with or without auth)
  useEffect(() => {
    if (fromCurrency !== toCurrency) {
      setExchangeRate(null)
      setToAmount(null)
    }
    fetchExchangeRate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromCurrency, toCurrency]) // fetchExchangeRate callback updates when these change

  // Recalculate when amount changes (but not when rate changes - that's handled in fetchExchangeRate)
  useEffect(() => {
    if (exchangeRate !== null && exchangeRate !== undefined) {
      setToAmount(fromAmount * exchangeRate)
    } else if (fromCurrency === toCurrency) {
      // Same currency - 1:1 conversion
      setToAmount(fromAmount)
    }
  }, [fromAmount, exchangeRate, fromCurrency, toCurrency])

  // Auto-refresh every 10 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      fetchExchangeRate()
    }, 10 * 60 * 1000) // 10 minutes – exchange rates don't change that fast

    return () => clearInterval(interval)
  }, [fetchExchangeRate])

  // Fetch unread notification count when logged in (404 = notifications API not deployed yet, show 0)
  const refreshUnreadCount = useCallback(async () => {
    if (!getAuthToken()) {
      setUnreadNotificationCount(0)
      return
    }
    try {
      const count = await getUnreadCount()
      setUnreadNotificationCount(count)
    } catch (_err) {
      // 404 = route not found (e.g. old backend); network/auth errors: show 0
      setUnreadNotificationCount(0)
    }
  }, [])

  useEffect(() => {
    refreshUnreadCount()
    const interval = setInterval(refreshUnreadCount, 5 * 60 * 1000) // 5 minutes
    return () => clearInterval(interval)
  }, [refreshUnreadCount])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (currencyRef.current && !currencyRef.current.contains(event.target as Node)) {
        setIsCurrencyOpen(false)
      }
    }

    if (isCurrencyOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isCurrencyOpen])

  return (
    <header className="bg-white border-b border-gray-200 fixed top-0 w-full" style={{ zIndex: 40 }}>
      <div className="flex items-center">
        {/* Logo - Left side with specific dimensions */}
        <div className="hidden lg:flex items-center border-b border-gray-200 border-r" style={{ 
          width: '242px', 
          height: '92px',
          paddingTop: '16px',
          paddingRight: '58px',
          paddingBottom: '16px',
          paddingLeft: '58px',
          gap: '4px',
          borderRightColor: '#E9E9EA',
          borderRightWidth: '1px'
        }}>
          <Logo size="header" showTagline={true} />
        </div>
        
        {/* Mobile Logo and Menu */}
        <div className="lg:hidden flex items-center gap-3 px-4" style={{ 
          height: '92px',
          minWidth: 'auto'
        }}>
          <button
            onClick={onMenuClick}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            aria-label="Toggle menu"
          >
            <FiMenu className="w-6 h-6" />
          </button>
          <Logo size="header" showTagline={false} />
        </div>

        {/* Main header content - Matches dashboard content width */}
        <div 
          className="flex items-center justify-between flex-1"
          style={{
            height: '92px',
            paddingLeft: '24px',
            paddingRight: '24px'
          }}
        >
          {/* Search Bar and Currency Converter */}
          <div className="hidden md:flex items-center gap-2 lg:gap-3 flex-1 lg:flex-initial min-w-0">
            {/* Search Bar - context-aware: searches current module, or Shipments when on dashboard/etc. */}
            <div className="relative">
                <button
                  type="button"
                  onClick={runSearch}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label="Search"
                >
                  <FiSearch className="w-5 h-5" />
                </button>
                <input
                  type="text"
                  placeholder="Search something here..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') runSearch()
                  }}
                  className="pl-10 pr-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  style={{
                    width: '359px',
                    height: '44px',
                    border: '1px solid #E9E9EA'
                  }}
                />
            </div>

            {/* Currency Exchange Rate - Single Box */}
            <div className="hidden lg:flex relative" ref={currencyRef}>
              {/* Main Currency Box - Fixed Size */}
              <div 
                className="flex items-center rounded-lg bg-white flex-shrink-0"
                style={{
                  width: '268px',
                  height: '44px',
                  border: '1px solid #E9E9EA'
                }}
              >
                {/* Exchange Rate Display */}
                <div className="px-4 flex-1 min-w-0">
                  {loading ? (
                    <span 
                      className="font-lexend whitespace-nowrap"
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 300,
                        fontSize: '12px',
                        lineHeight: '20px',
                        color: '#69697A'
                      }}
                    >
                      Loading...
                    </span>
                  ) : error ? (
                    <span 
                      className="font-lexend whitespace-nowrap"
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 300,
                        fontSize: '12px',
                        lineHeight: '20px',
                        color: '#E93323'
                      }}
                    >
                      Error
                    </span>
                  ) : exchangeRate !== null ? (
                    <span 
                      className="font-lexend whitespace-nowrap"
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 300,
                        fontSize: '12px',
                        lineHeight: '20px',
                        color: '#212126'
                      }}
                    >
                      1.00 {fromCurrency} = {exchangeRate.toFixed(2)} {toCurrency}
                    </span>
                  ) : fromCurrency === toCurrency ? (
                    <span 
                      className="font-lexend whitespace-nowrap"
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 300,
                        fontSize: '12px',
                        lineHeight: '20px',
                        color: '#212126'
                      }}
                    >
                      1.00 {fromCurrency} = 1.00 {toCurrency}
                    </span>
                  ) : (
                    <span 
                      className="font-lexend whitespace-nowrap"
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 300,
                        fontSize: '12px',
                        lineHeight: '20px',
                        color: '#69697A'
                      }}
                    >
                      Select currencies
                    </span>
                  )}
                </div>

                {/* Currency Selector */}
                <button
                  onClick={() => setIsCurrencyOpen(!isCurrencyOpen)}
                  className="flex items-center gap-2 px-4 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent rounded-r-lg h-full"
                >
                  <span className="text-sm text-gray-700">{fromCurrency}/{toCurrency}</span>
                  <FiChevronUp className={`w-4 h-4 text-gray-500 transition-transform ${isCurrencyOpen ? 'rotate-180' : ''}`} />
                </button>
              </div>

              {/* Dropdown Currency Converter Box - Appears Below */}
              {isCurrencyOpen && (
                <div className="absolute top-full left-0 mt-1 w-full border border-gray-300 rounded-lg bg-white shadow-lg z-[100]">
                  <div className="p-4">
                    {/* Currency Input Boxes with Converter Icon - Vertical Layout */}
                    <div className="flex flex-col items-center gap-2 mb-4">
                      {/* First Box - From Currency */}
                      <div 
                        className="flex items-center justify-between px-3 border border-gray-300 rounded-lg bg-white w-full"
                        style={{ height: '36px', maxWidth: '244px' }}
                      >
                        <input
                          type="number"
                          value={fromAmount}
                          onChange={(e) => setFromAmount(parseFloat(e.target.value) || 0)}
                          step="0.01"
                          min="0"
                          className="text-sm text-gray-700 border-none outline-none bg-transparent flex-1"
                          style={{
                            fontFamily: 'Lexend Deca',
                            fontWeight: 400,
                            fontSize: '14px',
                            width: '60px'
                          }}
                        />
                        <div className="flex items-center gap-2">
                          {/* Currency Dropdown - includes flag in trigger button */}
                          <CurrencyDropdown
                            value={fromCurrency}
                            onChange={(code) => {
                              console.log(`[Currency Change] From: ${fromCurrency} → ${code}`)
                              setFromCurrency(code)
                            }}
                            excludeCurrency={toCurrency}
                          />
                        </div>
                      </div>

                      {/* Converter Icon with Swap Button */}
                      <button
                        onClick={swapCurrencies}
                        className="flex items-center justify-center border border-gray-300 rounded-full bg-white flex-shrink-0 hover:bg-gray-50 transition-colors"
                        style={{ width: '32px', height: '32px', padding: '4px' }}
                        title="Swap currencies"
                        aria-label="Swap currencies"
                      >
                        <FiRefreshCw 
                          className="text-gray-600 w-5 h-5" 
                          style={{ transform: 'rotate(90deg)' }}
                        />
                      </button>

                      {/* Second Box - To Currency */}
                      <div 
                        className="flex items-center justify-between px-3 border border-gray-300 rounded-lg bg-white w-full"
                        style={{ height: '36px', maxWidth: '244px' }}
                      >
                        <span className="text-sm text-gray-700">
                          {toAmount !== null && exchangeRate !== null 
                            ? formatCurrency(toAmount, toCurrency) 
                            : fromCurrency === toCurrency 
                              ? formatCurrency(fromAmount, toCurrency)
                              : '--'}
                        </span>
                        <div className="flex items-center gap-2">
                          {/* Currency Dropdown - includes flag in trigger button */}
                          <CurrencyDropdown
                            value={toCurrency}
                            onChange={(code) => {
                              console.log(`[Currency Change] To: ${toCurrency} → ${code}`)
                              setToCurrency(code)
                            }}
                            excludeCurrency={fromCurrency}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Source Link */}
                    <div className="flex justify-center">
                      <a
                        href="https://xe.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                        style={{ height: '22px' }}
                      >
                        <FiExternalLink className="w-3 h-3" />
                        <span>xe.com</span>
                        <span className="text-gray-600">0.05%</span>
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* AI Actions */}
            <div className="hidden lg:flex items-center flex-shrink-0">
              <Link
                to={ROUTES.AI_ACTIONS}
                className="flex items-center gap-2 xl:gap-3 rounded-lg hover:opacity-90 transition-opacity"
                style={{
                  width: 'auto',
                  height: '44px',
                  border: '1px solid #212126',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  backgroundColor: '#F7F7F7',
                  maxWidth: '100%',
                  textDecoration: 'none',
                }}
              >
                <div className="flex-shrink-0 flex items-center justify-center text-[#212126]">
                  <FiZap className="w-5 h-5 xl:w-6 xl:h-6" />
                </div>
                <div className="flex flex-col items-start flex-shrink-0 min-w-0">
                  <span
                    className="text-[9px] xl:text-[10px] whitespace-nowrap font-medium"
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 600,
                      fontStyle: 'normal',
                      fontSize: '12px',
                      lineHeight: '20px',
                      letterSpacing: '0%',
                      color: '#212126',
                    }}
                  >
                    AI Actions
                  </span>
                  <span
                    className="text-[9px] xl:text-[10px] whitespace-nowrap"
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 300,
                      fontStyle: 'normal',
                      fontSize: '9px',
                      lineHeight: '20px',
                      letterSpacing: '0%',
                      color: '#69697A',
                    }}
                  >
                    Pending recommendations
                  </span>
                </div>
              </Link>
            </div>
          </div>

          {/* Right Side Actions - Aligned to match dashboard content right edge */}
          <div className="flex items-center gap-2 lg:gap-3 flex-shrink-0">
            {/* Search icon for mobile */}
            <button className="md:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md">
              <FiSearch className="w-5 h-5" />
            </button>

            {/* Notifications */}
            <button
              onClick={() => setIsNotificationModalOpen(true)}
              className="relative flex items-center justify-center border border-gray-300 rounded-full bg-white hover:bg-gray-50 transition-colors"
              style={{ width: '44px', height: '44px' }}
            >
              {/* Vuesax Linear Notification Icon */}
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-gray-600">
                <path d="M12.02 2.91C8.71 2.91 6.02 5.6 6.02 8.91V11.8C6.02 12.41 5.76 13.34 5.45 13.86L4.3 15.77C3.59 16.95 4.08 18.26 5.38 18.7C9.69 20.14 14.32 20.14 18.63 18.7C19.86 18.3 20.41 16.87 19.73 15.77L18.58 13.86C18.28 13.34 18.02 12.41 18.02 11.8V8.91C18.02 5.61 15.32 2.91 12.02 2.91Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14.87 3.2C14.56 3.11 14.24 3.04 13.91 3C12.95 2.79 12.01 2.79 11.08 3C11.17 2.99 11.26 2.98 11.35 2.97C12.41 2.87 13.46 2.95 14.49 3.2H14.87Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M15.02 19.06C15.02 20.71 13.67 22.06 12.02 22.06C10.37 22.06 9.02 20.71 9.02 19.06" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              
              {/* Notification Badge - only when unread */}
              {unreadNotificationCount > 0 && (
                <div
                  className="absolute rounded-full flex items-center justify-center"
                  style={{
                    minWidth: '18px',
                    height: '18px',
                    top: '4px',
                    right: '4px',
                    backgroundColor: '#E93323',
                    border: '1px solid #FFFFFF',
                    fontSize: '10px',
                    color: '#FFF',
                    fontFamily: 'Lexend Deca',
                    padding: '0 4px',
                  }}
                >
                  {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                </div>
              )}
            </button>

            {/* Messages (SMS) */}
            <Link
              to="#"
              className="relative flex items-center justify-center border border-gray-300 rounded-full bg-white hover:bg-gray-50 transition-colors"
              style={{ width: '44px', height: '44px' }}
            >
              {/* Vuesax Linear SMS Icon */}
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-gray-600">
                <path d="M17 20.5H7C4 20.5 2 19 2 15.5V8.5C2 5 4 3.5 7 3.5H17C20 3.5 22 5 22 8.5V15.5C22 19 20 20.5 17 20.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M17 9L13.87 11.5C12.84 12.32 11.15 12.32 10.12 11.5L7 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>

            {/* Sign in when not logged in; Sign out when inside AuthProvider (Okta or legacy) */}
            {!getAuthToken() ? (
              <Link
                to="/login"
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium transition-colors"
              >
                Sign in
              </Link>
            ) : auth ? (
              <button
                type="button"
                onClick={() => auth.logout()}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium transition-colors"
                title="Sign out"
              >
                <FiLogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Sign out</span>
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {/* Notification Modal */}
      <NotificationModal
        isOpen={isNotificationModalOpen}
        onClose={() => setIsNotificationModalOpen(false)}
        onMarkedRead={refreshUnreadCount}
      />
    </header>
  )
}

export default Header
